import { useState, useEffect, useCallback } from "react";
import { Eye, Download, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import styles from "./TransactionList.module.css";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import ViewTransactionModal from "../../../components/Finance/Payments/ViewTransactionModal";
import { getTransactions, normalizeTransaction } from "../../../api/finance/transactions";
import { useToast } from "../../../context/ToastContext";

const fmt = (n) =>
  `€ ${Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TransactionList = () => {
  const { showToast } = useToast();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [directionFilter, setDirectionFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({ total_incoming: 0, total_outgoing: 0, net_balance: 0, incoming_transactions: 0, outgoing_transactions: 0 });
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const fetchTransactions = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTransactions({
        page: 1,
        limit: 100,
        search: params.search ?? search,
        client: params.typeFilter ?? typeFilter,
        from_date: params.dateFrom ?? dateFrom,
        to_date: params.dateTo ?? dateTo,
      });
      setTransactions((res.data || []).map(normalizeTransaction));
      if (res.meta?.summary) setSummary(res.meta.summary);
      if (res.meta?.pagination) setPagination(res.meta.pagination);
    } catch (err) {
      setError("Failed to load transactions");
      showToast(err.message || "Failed to load transactions", "error");
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, dateFrom, dateTo]);

  useEffect(() => { fetchTransactions(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const s = searchInput.trim();
    setSearch(s);
    fetchTransactions({ search: s });
  };

  const applyFilter = (updates) => {
    const next = { search, typeFilter, dateFrom, dateTo, ...updates };
    if (updates.typeFilter !== undefined) setTypeFilter(updates.typeFilter);
    if (updates.dateFrom !== undefined) setDateFrom(updates.dateFrom);
    if (updates.dateTo !== undefined) setDateTo(updates.dateTo);
    fetchTransactions(next);
  };

  const TYPE_TO_API = {
    "Client Payment": "incoming_payment",
    "Supplier Payment": "outgoing_payment",
    "Client Refund": "refund",
    "Supplier Refund": "refund",
    "": "",
  };

  const filteredTransactions = transactions.filter((txn) => {
    const matchesDirection = directionFilter === "" || txn.direction === directionFilter;
    return matchesDirection;
  });

  const handleView = (transaction) => {
    setSelectedTransaction(transaction);
    setShowViewModal(true);
  };

  const handleExport = () => {
    window.open(`/api/finance/payments/transactions?exportType=csv`, "_blank");
  };

  const getDirectionBadgeClass = (direction) => {
    return `${styles.directionBadge} ${direction === "Incoming" ? styles.directionIncoming : styles.directionOutgoing}`;
  };

  const getTypeBadgeClass = (type) => {
    const map = {
      "Client Payment": styles.typeClientPayment,
      "Supplier Payment": styles.typeSupplierPayment,
      "Client Refund": styles.typeClientRefund,
      "Supplier Refund": styles.typeSupplierRefund,
    };
    return `${styles.typeBadge} ${map[type] || ""}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Transaction List</div>
        <button className={styles.exportBtn} onClick={handleExport}>
          <Download size={16} />
          Export
        </button>
      </div>

      <div className={styles.infoBar}>
        <span className={styles.infoIcon} aria-hidden>ℹ️</span>
        <span>
          Central audit and reporting list of all financial transactions. This includes incoming client payments,
          outgoing supplier payments, and all refund transactions.
        </span>
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryIcon} style={{ background: '#d1fae5', color: '#059669' }}>
            <ArrowDownCircle size={20} />
          </div>
          <div className={styles.summaryContent}>
            <div className={styles.summaryLabel}>Total Incoming</div>
            <div className={styles.summaryValue} style={{ color: '#059669' }}>
              {fmt(summary.total_incoming)}
            </div>
            <div className={styles.summaryCount}>{summary.incoming_transactions} transactions</div>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.summaryIcon} style={{ background: '#fee2e2', color: '#dc2626' }}>
            <ArrowUpCircle size={20} />
          </div>
          <div className={styles.summaryContent}>
            <div className={styles.summaryLabel}>Total Outgoing</div>
            <div className={styles.summaryValue} style={{ color: '#dc2626' }}>
              {fmt(summary.total_outgoing)}
            </div>
            <div className={styles.summaryCount}>{summary.outgoing_transactions} transactions</div>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.summaryIcon} style={{ background: '#dbeafe', color: '#3b82f6' }}>
            <Download size={20} />
          </div>
          <div className={styles.summaryContent}>
            <div className={styles.summaryLabel}>Net Balance</div>
            <div className={styles.summaryValue} style={{ color: summary.net_balance >= 0 ? '#059669' : '#dc2626' }}>
              {fmt(Math.abs(summary.net_balance))}
            </div>
            <div className={styles.summaryCount}>{summary.net_balance >= 0 ? 'Positive' : 'Negative'} balance</div>
          </div>
        </div>
      </div>

      <div className={styles.filterBar}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search by transaction #, booking, PNR, supplier ref, or entity..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.addbtn}>Search</button>
        </form>

        <input
          type="date"
          value={dateFrom}
          onChange={(e) => applyFilter({ dateFrom: e.target.value })}
          className={styles.dateInput}
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => applyFilter({ dateTo: e.target.value })}
          className={styles.dateInput}
        />

        <SelectBox
          placeholder="Direction: All"
          value={directionFilter}
          options={[
            { label: "All Directions", value: "" },
            { label: "Incoming", value: "Incoming" },
            { label: "Outgoing", value: "Outgoing" },
          ]}
          onChange={(e) => setDirectionFilter(e.target.value)}
        />

        <SelectBox
          placeholder="Type: All"
          value={typeFilter}
          options={[
            { label: "All Types", value: "" },
            { label: "Client Payment", value: "client_payment" },
            { label: "Supplier Payment", value: "supplier_payment" },
            { label: "Client Refund", value: "client_refund" },
            { label: "Supplier Refund", value: "supplier_refund" },
          ]}
          onChange={(e) => applyFilter({ typeFilter: e.target.value })}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading transactions...</div>
      ) : (
        <>
          <div className={styles.meta}>
            Showing {filteredTransactions.length} of {pagination.total} transactions
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>TXN #</th>
                  <th>DATE</th>
                  <th>DIRECTION</th>
                  <th>TYPE</th>
                  <th>ENTITY</th>
                  <th>BOOKING REF</th>
                  <th>ACCOUNT</th>
                  <th>AMOUNT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  filteredTransactions.map((txn) => (
                    <tr key={txn.id}>
                      <td className={styles.txnRef}>{txn.transactionRef}</td>
                      <td className={styles.date}>{txn.date}</td>
                      <td>
                        <span className={getDirectionBadgeClass(txn.direction)}>
                          {txn.direction === "Incoming" ? <ArrowDownCircle size={14} /> : <ArrowUpCircle size={14} />}
                          {txn.direction}
                        </span>
                      </td>
                      <td>
                        <span className={getTypeBadgeClass(txn.type)}>{txn.type}</span>
                      </td>
                      <td>
                        <div className={styles.entityInfo}>
                          <div className={styles.entityName}>{txn.entityName}</div>
                          {txn.entityEmail && <div className={styles.entityEmail}>{txn.entityEmail}</div>}
                        </div>
                      </td>
                      <td><span className={styles.bookingRef}>{txn.bookingRef}</span></td>
                      <td className={styles.account}>{txn.account}</td>
                      <td className={`${styles.amount} ${txn.direction === "Incoming" ? styles.amountIncoming : styles.amountOutgoing}`}>
                        {txn.direction === "Incoming" ? "+" : "-"}€ {txn.amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td>
                        <div className={styles.actions}>
                          <button className={styles.actionBtn} onClick={() => handleView(txn)} title="View transaction">
                            <Eye size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ViewTransactionModal
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        transaction={selectedTransaction}
      />
    </div>
  );
};

export default TransactionList;
